import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { mediaUrl } from "@/lib/api";
import { Play, Heart, Eye, Music2, Film, ArrowUpRight, MessageSquare, Share2 } from "lucide-react";
import ShareDialog from "@/components/ShareDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicProfile() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/profile/${handle}`);
        setData(data);
      } catch (e) {
        setError(e?.response?.status === 404 ? "Profile not found" : "Could not load profile");
      }
    })();
  }, [handle]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f5] flex items-center justify-center grain">
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#ff4500] font-mono">── 404</div>
          <h1 className="font-display text-4xl mt-3 tracking-tighter">{error}</h1>
          <Link to="/" className="inline-block mt-6 px-6 py-3 bg-[#ff4500] hover:bg-[#ff5722] text-white text-sm">Go to Rasin 🇭🇹</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#0a0a0a] text-[#a3a3a3] flex items-center justify-center text-sm tracking-[0.2em] uppercase">Loading</div>;
  }

  const { user, videos, audios, stats } = data;
  const shareUrl = `${window.location.origin}/u/${user.handle}`;
  const ogUrl = `${BACKEND_URL}/api/og/u/${user.handle}`;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f5] grain">
      {/* Top bar */}
      <header className="flex items-center justify-between px-6 sm:px-10 py-5 border-b border-[#2a2a2a]">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 flex flex-col overflow-hidden shadow-lg">
            <div className="flex-1 bg-[#00209F]" />
            <div className="flex-1 bg-[#D21034]" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display font-semibold tracking-tight text-lg">Rasin</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3] mt-1">Haiti's Stage · 🇭🇹</span>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/login" className="px-4 py-2 text-xs uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#f5f5f5]">Sign in</Link>
          <Link to="/register" data-testid="public-cta-register" className="px-5 py-2 text-xs uppercase tracking-[0.2em] bg-[#ff4500] hover:bg-[#ff5722] text-white">Join free</Link>
        </div>
      </header>

      {/* Profile hero */}
      <section className="px-6 sm:px-10 py-14 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row gap-8 items-start sm:items-end">
          <div className="w-28 h-28 sm:w-36 sm:h-36 bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center overflow-hidden">
            {user.avatar_url ? (
              <img src={mediaUrl(user.avatar_url)} alt={user.name} className="w-full h-full object-cover" />
            ) : (
              <span className="font-display text-5xl text-[#ff4500]">{user.name?.[0]?.toUpperCase() || "U"}</span>
            )}
          </div>
          <div className="flex-1 reveal">
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#ff4500] font-mono">── Creator</div>
            <h1 data-testid="public-profile-name" className="font-display text-5xl sm:text-6xl tracking-tighter mt-2">{user.name}</h1>
            <div className="text-sm text-[#a3a3a3] mt-3 font-mono uppercase tracking-[0.2em]">@{user.handle}</div>
            {user.bio && <p className="text-[#a3a3a3] mt-4 max-w-xl leading-relaxed">{user.bio}</p>}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button onClick={() => navigate("/register")} data-testid="public-message-cta" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#ff4500] hover:bg-[#ff5722] text-white text-xs uppercase tracking-[0.2em]">
                <MessageSquare className="w-4 h-4" /> Sign up to message
              </button>
              <button onClick={() => setShareOpen(true)} data-testid="public-share-btn" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#ff4500] text-xs uppercase tracking-[0.2em]">
                <Share2 className="w-4 h-4" /> Share profile
              </button>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#a3a3a3] ml-1">
                {stats.videos} videos · {stats.tracks} tracks
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Videos */}
      {videos.length > 0 && (
        <section className="px-6 sm:px-10 pb-14 max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-5">
            <Film className="w-5 h-5 text-[#ff4500]" />
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight">Videos</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map((v) => (
              <Link key={v.id} to={`/v/${v.id}`} data-testid={`public-video-${v.id}`} className="group bg-[#141414] border border-[#2a2a2a] hover:border-[#ff4500]/50 transition-colors rounded-sm overflow-hidden">
                <div className="relative aspect-video bg-black">
                  {v.thumbnail_url ? (
                    <img src={mediaUrl(v.thumbnail_url)} alt={v.title} className="w-full h-full object-cover" />
                  ) : (
                    <video src={mediaUrl(v.video_url)} className="w-full h-full object-cover" muted preload="metadata" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                    <div className="w-14 h-14 bg-[#ff4500] flex items-center justify-center"><Play className="w-6 h-6 text-white ml-1" /></div>
                  </div>
                  <div className="absolute bottom-2 right-3 flex items-center gap-3 text-[11px] text-white/80">
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{v.views}</span>
                    <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{v.like_count}</span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-display text-base leading-snug line-clamp-2">{v.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Audios */}
      {audios.length > 0 && (
        <section className="px-6 sm:px-10 pb-20 max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-5">
            <Music2 className="w-5 h-5 text-[#ff4500]" />
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight">Tracks</h2>
          </div>
          <div className="border border-[#2a2a2a]">
            {audios.map((a, i) => (
              <div key={a.id} className="flex items-center justify-between px-4 py-3 border-b border-[#2a2a2a] last:border-b-0 hover:bg-[#141414] transition-colors" data-testid={`public-audio-${a.id}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <span className="text-[10px] font-mono text-[#a3a3a3] w-6">{String(i + 1).padStart(2, "0")}</span>
                  <div className="min-w-0">
                    <div className="font-display text-sm truncate">{a.title}</div>
                    <div className="text-xs text-[#a3a3a3] truncate">{a.artist}</div>
                  </div>
                </div>
                <audio src={mediaUrl(a.audio_url)} controls className="hidden sm:block max-w-xs" />
              </div>
            ))}
          </div>
        </section>
      )}

      {videos.length === 0 && audios.length === 0 && (
        <section className="px-6 sm:px-10 pb-20 max-w-6xl mx-auto">
          <div className="border border-dashed border-[#2a2a2a] p-16 text-center">
            <p className="text-[#a3a3a3] text-sm">{user.name} hasn't uploaded anything yet.</p>
          </div>
        </section>
      )}

      {/* Footer CTA */}
      <section className="px-6 sm:px-10 py-16 border-t border-[#2a2a2a] bg-[#141414]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl tracking-tighter">Have your own stage on Rasin 🇭🇹</h2>
          <p className="text-[#a3a3a3] mt-3 text-sm">Upload videos, host tracks, message creators. Free forever.</p>
          <Link to="/register" className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[#ff4500] hover:bg-[#ff5722] text-white text-sm">
            Create your profile <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {shareOpen && (
        <ShareDialog
          title={`${user.name} on Rasin · Haiti's Stage`}
          text={user.bio || `Check out ${user.name} on Rasin 🇭🇹`}
          shareUrl={shareUrl}
          ogUrl={ogUrl}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}
